/**
 * ContractMiniCard — card riassuntiva di un contratto/POD nella pagina Profilo.
 *
 * Replica del mobile (`Profile.js > ContractMiniCard`):
 *  - icona casa cyan in box
 *  - etichetta personalizzata (se presente) + indirizzo
 *  - meta: POD + potenza disponibile
 *  - pallino "Attiva" verde se è il contratto attivo
 *  - bordo cyan se attivo
 *  - click → apre dettaglio (al momento placeholder /profilo/contratto/:codice)
 */

import { Eye, Home } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import type { Contratto } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

type Props = {
  contract: Contratto
  isActive: boolean
  isMonitored?: boolean
}

export function ContractMiniCard({ contract, isActive, isMonitored }: Props) {
  const etichetta = contract.pod?.etichetta?.trim() || null
  const indirizzo = contract.pod?.indirizzo?.trim() || ''
  const pod = contract.pod?.pod?.trim() || ''
  const potenza = contract.potenza_disponibile

  return (
    <Link
      to={`/profilo/contratto/${encodeURIComponent(contract.codice_contratto)}`}
      className="block"
    >
      <Card
        interactive
        className={cn(
          'flex items-start gap-3 p-4',
          isActive && 'border-secondary border-2',
        )}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
          {isMonitored ? <Eye className="h-4 w-4" /> : <Home className="h-4 w-4" />}
        </div>

        <div className="flex flex-1 flex-col gap-1 min-w-0">
          {etichetta ? (
            <>
              <p className="truncate text-sm font-semibold text-foreground">
                {etichetta}
              </p>
              {indirizzo && (
                <p className="truncate text-xs text-muted-foreground">{indirizzo}</p>
              )}
            </>
          ) : (
            <p className="line-clamp-2 text-sm font-medium text-foreground">
              {indirizzo || 'Contratto'}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            <span className="font-mono">POD: {pod}</span>
            {potenza && (
              <>
                <span aria-hidden>·</span>
                <span>{potenza} kW</span>
              </>
            )}
          </div>

          {isActive && (
            <span className="mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-semibold text-success">
              <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
              Attivo
            </span>
          )}
        </div>
      </Card>
    </Link>
  )
}
